<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useStore } from 'vuex';
import Twilio from './Twilio.vue';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import Evolution from './Evolution.vue';
import Waha from './Waha.vue';
import Uazapi from './Uazapi.vue';
import Notificame from './Notificame.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();

const PROVIDER_TYPES = {
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  TWILIO: 'twilio',
  THREE_SIXTY_DIALOG: '360dialog',
  EVOLUTION: 'evolution',
  WAHA: 'waha',
  UAZAPI: 'uazapi',
  NOTIFICAME: 'notificame',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const isWhatsappEvolutionEnabled = computed(() => {
  const accountId = route.params.accountId;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    FEATURE_FLAGS.WHATSAPP_EVOLUTION
  );
});

const selectedProvider = computed(() => {
  const provider = route.query.provider;

  if (!provider) {
    return null;
  }

  if (provider === 'whatsapp' || provider === 'whatsapp_cloud') {
    return PROVIDER_TYPES.WHATSAPP_MANUAL;
  }

  return provider;
});

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

const availableProviders = computed(() => {
  const providers = [];

  if (hasWhatsappAppId.value) {
    providers.push({
      key: PROVIDER_TYPES.WHATSAPP_EMBEDDED,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_EMBEDDED'),
      description: t(
        'INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_EMBEDDED_DESC'
      ),
      icon: 'i-woot-whatsapp',
    });
  }

  if (!hasWhatsappAppId.value) {
    providers.push({
      key: PROVIDER_TYPES.WHATSAPP_MANUAL,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD_DESC'),
      icon: 'i-woot-whatsapp',
    });
  }

  providers.push({
    key: PROVIDER_TYPES.TWILIO,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO_DESC'),
    icon: 'i-woot-twilio',
  });

  providers.push({
    key: PROVIDER_TYPES.NOTIFICAME,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.NOTIFICAME'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.NOTIFICAME_DESC'),
    icon: 'i-woot-whatsapp',
  });

  if (isWhatsappEvolutionEnabled.value) {
    providers.push({
      key: PROVIDER_TYPES.EVOLUTION,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.EVOLUTION'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.EVOLUTION_DESC'),
      icon: 'i-woot-whatsapp',
      isBeta: true,
    });
    providers.push({
      key: PROVIDER_TYPES.WAHA,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA_DESC'),
      icon: 'i-woot-whatsapp',
      isBeta: true,
    });
    providers.push({
      key: PROVIDER_TYPES.UAZAPI,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.UAZAPI'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.UAZAPI_DESC'),
      icon: 'i-woot-whatsapp',
      isBeta: true,
    });
  }

  return providers;
});

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowCloudWhatsapp = provider => {
  return provider === PROVIDER_TYPES.WHATSAPP_MANUAL;
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <div class="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
        <div
          v-for="provider in availableProviders"
          :key="provider.key"
          class="relative"
        >
          <ChannelSelector
            class="h-full w-full"
            :class="{
              'opacity-50 cursor-not-allowed pointer-events-none':
                provider.isDisabled,
            }"
            :title="provider.title"
            :description="provider.description"
            :icon="provider.icon"
            @click="!provider.isDisabled && selectProvider(provider.key)"
          />
          <span
            v-if="provider.isComingSoon"
            class="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-200 dark:bg-slate-700 rounded"
          >
            {{ $t('COMPONENTS.COMING_SOON') }}
          </span>
        </div>
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <div class="px-6 py-5 rounded-2xl border border-n-weak">
        <!-- Show embedded signup if app ID is configured -->
        <div v-if="selectedProvider === PROVIDER_TYPES.WHATSAPP_EMBEDDED">
          <WhatsappEmbeddedSignup />

          <!-- Manual setup fallback option -->
          <div class="pt-6 mt-6 border-t border-n-weak">
            <I18nT
              keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
              tag="p"
              class="text-sm text-n-slate-11"
            >
              <template #link>
                <a
                  href="#"
                  class="underline text-n-brand"
                  @click.prevent="handleManualLinkClick"
                >
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
                    )
                  }}
                </a>
              </template>
            </I18nT>
          </div>
        </div>

        <!-- Show manual setup -->
        <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />

        <!-- Other providers -->
        <Twilio
          v-else-if="selectedProvider === PROVIDER_TYPES.TWILIO"
          type="whatsapp"
        />
        <ThreeSixtyDialogWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
        />
        <Evolution v-else-if="selectedProvider === PROVIDER_TYPES.EVOLUTION" />
        <Waha v-else-if="selectedProvider === PROVIDER_TYPES.WAHA" />
        <Uazapi v-else-if="selectedProvider === PROVIDER_TYPES.UAZAPI" />
        <Notificame
          v-else-if="selectedProvider === PROVIDER_TYPES.NOTIFICAME"
        />
        <CloudWhatsapp v-else />
      </div>
    </div>
  </div>
</template>
